import type { FC } from 'react';
import {
  DevHubBox,
  DevHubFormControlLabel,
  DevHubSwitch,
  DevHubTypography,
  DevHubTooltip,
} from '@devxdiscover/devhub-ui';
import { FunctionalAreaSelector } from './index';

export interface PageHeaderSectionProps {
  // Functional Area Selector props
  selectedFunctionalArea: string;
  onFunctionalAreaChange: (area: string) => void;
  functionalAreas: string[];
  isLoadingFunctionalAreas?: boolean;

  // Admin toggle props
  isAdmin?: boolean;
  showAllFunctionalAreas?: boolean;
  onAdminToggle?: (checked: boolean) => void;

  // Loading banner
  children?: React.ReactNode;
}

/**
 * PageHeaderSection component for managing the page header section
 * @param param0 - Props for the component
 * @returns PageHeaderSection JSX element
 */
export const PageHeaderSection: FC<PageHeaderSectionProps> = ({
  selectedFunctionalArea,
  onFunctionalAreaChange,
  functionalAreas,
  isLoadingFunctionalAreas = false,
  isAdmin = false,
  showAllFunctionalAreas = false,
  onAdminToggle,
  children,
}) => {
  return (
    <DevHubBox
      style={{
        marginTop: '2rem',
      }}
    >
      <DevHubBox
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '1rem',
          marginBottom: isAdmin ? '1rem' : 0,
        }}
      >
        <FunctionalAreaSelector
          selectedFunctionalArea={selectedFunctionalArea}
          onSelectionChange={onFunctionalAreaChange}
          functionalAreas={functionalAreas}
          isLoadingFunctionalAreas={isLoadingFunctionalAreas}
        />

        {/* Admin Toggle */}
        {isAdmin && onAdminToggle && (
          <DevHubTooltip
            title={
              showAllFunctionalAreas
                ? 'Currently viewing all functional areas. Toggle off to view only your assigned functional areas.'
                : 'Toggle on to manage data leads for all functional areas (admin privilege)'
            }
            placement="left"
          >
            <DevHubFormControlLabel
              control={
                <DevHubSwitch
                  checked={showAllFunctionalAreas}
                  onChange={e => onAdminToggle(e.target.checked)}
                  color="secondary"
                  style={{
                    color: showAllFunctionalAreas ? '#d32f2f' : undefined,
                  }}
                />
              }
              label={
                <DevHubTypography
                  variant="body2"
                  style={{
                    fontWeight: 600,
                    color: showAllFunctionalAreas ? '#c62828' : '#666',
                  }}
                >
                  View All Functional Areas
                </DevHubTypography>
              }
              style={{
                margin: 0,
                padding: '0.5rem 1rem',
                backgroundColor: showAllFunctionalAreas ? '#ffebee' : '#f5f5f5',
                borderRadius: '8px',
                border: '2px solid #ef5350',
                transition: 'all 0.2s ease-in-out',
                boxShadow: showAllFunctionalAreas
                  ? '0 2px 4px rgba(239, 83, 80, 0.2)'
                  : 'none',
              }}
            />
          </DevHubTooltip>
        )}
      </DevHubBox>

      {/* Additional content like loading banners */}
      {children}
    </DevHubBox>
  );
};
